import {
  AfterViewChecked,
  ChangeDetectorRef,
  Component,
  Inject,
  OnInit,
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { UserService } from 'src/app/core/api/user/user.service';
import { SnackBarService } from 'src/app/core/services/snackbar/snackbar.service';
import { BusinessUpdateRequestDTO } from 'src/app/shared/models/dtos/requests/business-update-request-dto/business-update-request-dto';
import { BusinessSummaryResponseDTO } from 'src/app/shared/models/dtos/responses/business-summary-response-dto/business-summary-response-dto';
import { Country } from 'src/app/shared/models/dtos/responses/country/country';
import { TranslationService } from 'src/app/core/services/translation/translation.service';
import {
  specialCharValidator,
  urlValidator,
} from 'src/app/shared/validators/registration-validators';
import { State } from 'src/app/shared/models/dtos/responses/state/state';
import {
  BusinessEmailAlreadyExists,
  BusinessFrozenException,
} from 'src/app/shared/errors/user-management-service/user-management-service-errors';

@Component({
  selector: 'app-modal',
  templateUrl: './edit-business-modal.component.html',
  styleUrls: ['./edit-business-modal.component.scss'],
})
/**
 * Modal for business editting
 */
export class EditBusinessModalComponent implements OnInit, AfterViewChecked {
  formGroup = new FormGroup({
    picture: new FormControl<string | null>(null),
    name: new FormControl<string | null>(null, [
      Validators.minLength(3),
      Validators.maxLength(255),
      specialCharValidator(),
    ]),
    address: new FormControl<string | null>(null, [
      Validators.minLength(3),
      Validators.maxLength(255),
    ]),
    number: new FormControl<string | null>(null, [Validators.maxLength(255)]),
    country: new FormControl<Country | null>(null, [Validators.required]),
    postalCode: new FormControl<string | null>(null, [
      Validators.minLength(3),
      Validators.maxLength(255),
    ]),
    businessType: new FormControl<string | null>(null),
    email: new FormControl<string | null>(null, [
      Validators.email,
      Validators.minLength(3),
      Validators.maxLength(255),
    ]),
    website: new FormControl('', [
      urlValidator(),
      Validators.minLength(3),
      Validators.maxLength(255),
    ]),
    phoneNumber: new FormControl('', [
      Validators.minLength(7),
      Validators.maxLength(18),
      Validators.pattern(/^[^'"&\\><=+@`{}]*$/),
    ]),
    state: new FormControl<State | null>(null, [Validators.required]),
  });
  countries!: Country[];
  regions: any;
  states: any;
  businessTypes!: string[];
  originalData: any;
  snackBarLabels = {
    success: 'success',
    error: 'error',
    alreadyExists: 'business-email-already-exists',
    businessFrozen: 'business-is-frozen-exception',
  };
  loading: boolean = false;
  /**
   * @constructor
   */
  constructor(
    public dialogRef: MatDialogRef<EditBusinessModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: BusinessSummaryResponseDTO,
    private userService: UserService,
    private changeDetectorRef: ChangeDetectorRef,
    private snackBarService: SnackBarService,
    private translationService: TranslationService
  ) {}

  /**
   * On init
   */
  ngOnInit(): void {
    this.formGroup.setValue({
      picture: this.data.picture || null,
      name: this.data.name || null,
      address: this.data.address || null,
      number: this.data.addressNumber || null,
      country: this.data.country || null,
      postalCode: this.data.postalCode || null,
      businessType: this.data.businessType || null,
      email: this.data.email || null,
      website: this.data.website || null,
      phoneNumber: this.data.phoneNumber || null,
      state: new State(0, this.data.state) || null,
    });

    this.originalData = this.formGroup.value;
    this.userService.getCountries().subscribe((countries) => {
      countries.sort((a, b) => a.name.localeCompare(b.name));
      this.countries = countries;
    });

    this.userService.listCountries().subscribe((regions) => {
      this.regions = regions;
      this.listStates(true);
    });

    this.userService
      .getBusinessTypes()
      .subscribe((types) => (this.businessTypes = types));

    this.translationService.translateObject(this.snackBarLabels);
  }
  /**
   * Action to close the dialog
   */
  onCloseClick() {
    this.dialogRef.close();
  }

  /**
   * Rules for disabling confirm button
   * @return {boolean}
   */
  isConfirmButtonDisabled() {
    return this.formGroup.invalid || !this.isBusinessFormChanged();
  }

  /**
   * @return {boolean}
   */
  isBusinessFormChanged() {
    return (
      JSON.stringify(this.originalData) !== JSON.stringify(this.formGroup.value)
    );
  }

  /**
   * @param {string} controlName
   * @return {FormControl}
   */
  getControl(controlName: string): FormControl {
    return this.formGroup.get(controlName) as FormControl;
  }

  /**
   * @param {Country} country region to extract name from
   * @return {string} extracted country name
   */
  countryName(country: Country): string {
    return country.name;
  }

  /**
   * ngAfterViewChecked
   */
  ngAfterViewChecked(): void {
    this.changeDetectorRef.detectChanges();
  }

  /**
   * Call API
   */
  updateBusiness() {
    const business = new BusinessUpdateRequestDTO(
      this.formGroup.value.name!,
      this.formGroup.value.businessType!,
      this.formGroup.value.website!,
      this.formGroup.value.email!,
      this.formGroup.value.picture!,
      this.formGroup.value.number!,
      this.formGroup.value.postalCode!,
      this.formGroup.value.state!.name,
      this.formGroup.value.address!,
      this.formGroup.value.country!.region.id,
      this.formGroup.value.country?.id,
      this.data.businessLevels![0].id,
      this.formGroup.value.phoneNumber!,
      true
    );

    this.loading = true;

    this.userService.updateBusiness(this.data.id, business).subscribe({
      next: (data) => {
        this.loading = false;
        const businessData = {
          imgSource: data.imgSource,
          businessName: data.businessName,
          address: data.address,
          postalCode: data.postalCode,
          addressNumber: data.addressNumber,
          country: this.findCountryById(data.countryId!),
          email: data.email,
          businessType: data.businessType,
          phoneNumber: data.phoneNumber,
          state: data.state,
          website: data.website,
        };
        this.dialogRef.close(businessData);
      },
      error: (err) => {
        this.loading = false;
        if (err instanceof BusinessEmailAlreadyExists) {
          return this.snackBarService.error(this.snackBarLabels.alreadyExists);
        }
        if (err instanceof BusinessFrozenException) {
          return this.snackBarService.error(this.snackBarLabels.businessFrozen);
        }
        this.snackBarService.error(this.snackBarLabels.error);
      },
      complete: () => {
        this.snackBarService.success(this.snackBarLabels.success);
      },
    });
  }

  /**
   * Changes state dropdown
   * @param {boolean} firstCall
   */
  listStates(firstCall?: boolean) {
    if (!this.regions) {
      return;
    }
    const country = this.formGroup.get('country')?.value;
    const stateForm = this.formGroup.get('state');
    const region = this.regions.find((regionItem: any) => {
      return regionItem.id === country?.region.id;
    });
    const states = region.countries.find((countries: any) => {
      return countries.id === country?.id;
    }).provinces;
    // If current country has no states
    if (states.length === 0) {
      states.push({ id: 0, name: 'N/A' });
    } else {
      states.sort((a: any, b: any) => a.name.localeCompare(b.name));
    }
    // If there is already an state saved and if current country hasn't changed
    if (firstCall) {
      const currentState = states.find(
        (currentStateItem: any) => currentStateItem.name === this.data.state
      );
      this.states = states;
      stateForm?.setValue(currentState);
      return;
    }
    this.states = states;
    stateForm?.setValue(states[0]);
  }

  /**
   * @param {number} id
   * @return {Country}
   */
  private findCountryById(id: number) {
    return this.countries.find((country) => id === country.id);
  }
}
